import axios from 'axios';
import * as Sentry from '@sentry/react';
import { store } from '../../redux/configureStore';
import { setLoading, clearLoading } from '../../redux/actions/loading-actions';
import { addAlert } from '../../redux/actions/alert-actions';
import { clearUser } from '../../redux/actions/user-actions';
import { sessionExpired } from '../../redux/actions/session-actions';
import websocketService from '../../utils/web-socket-service';

/* eslint-disable import/prefer-default-export */

const buildApiBaseUrl = () => {
  const protocol = process.env.REACT_APP_SERVER_PROTOCOL || 'https';
  const host = process.env.REACT_APP_SERVER_DOMAIN_NAME || '';
  const prefix = process.env.REACT_APP_API_URL_PREFIX || '/api';
  const version = process.env.REACT_APP_API_VERSION || '';
  if (!host) {
    console.error(
      'REACT_APP_SERVER_DOMAIN_NAME is missing. Rebuild the frontend with it set (e.g. rentovault.com to use your nginx /api proxy).',
    );
  }
  return `${protocol}://${host}${prefix}${version}`;
};

export const getApiErrorMessage = (error, fallback = 'An error occurred, please try again or contact support.') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'object' && first.msg) {
      return first.msg;
    }
  }
  return fallback;
};

export const reportApiErrorToSentry = (
  error,
  {
    url,
    method,
    sentryTags = {},
    sentryExtra = {},
  } = {},
) => {
  const status = error?.response?.status;
  if (status === 401) {
    return;
  }

  const tags = {
    ...sentryTags,
    ...(url ? { api_url: url } : {}),
    ...(method ? { api_method: method } : {}),
    ...(status ? { http_status: String(status) } : {}),
  };
  const extra = {
    ...sentryExtra,
    status,
    detail: getApiErrorMessage(error, ''),
    responseData: error?.response?.data,
  };

  if (!error.response || status >= 500) {
    Sentry.captureException(error, { tags, extra });
    return;
  }

  if (status >= 400) {
    const message = extra.detail || `API ${method || '?'} ${url || '?'} failed with ${status}`;
    Sentry.captureMessage(message, {
      level: 'warning',
      tags,
      extra,
    });
  }
};

export const request = (url, method, opts = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (process.env.REACT_APP_USER_TYPE_HEADER) {
    headers[process.env.REACT_APP_USER_TYPE_HEADER] = 'customer';
  }

  if (opts.isFormData) {
    headers['Content-Type'] = 'multipart/form-data';
  }

  const fetchOptions = {
    method,
    headers,
  };

  if (opts.payload) {
    fetchOptions.data = opts.payload;
  }
  store.dispatch(setLoading());
  return new Promise((resolve, reject) => {
    axios({
      ...fetchOptions,
      ...opts.axiosOptions,
      withCredentials: true,
      url,
      baseURL: buildApiBaseUrl(),
      validateStatus: (status) => status >= 200 && status < 300,
    })
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          if (!opts.skipSessionRecovery) {
            store.dispatch(addAlert({ message: 'Your session has expired, please log in.', severity: 'error' }));
            const returnUrl = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : null;
            store.dispatch(sessionExpired({ returnUrl }));
          }
          store.dispatch(clearUser());
          websocketService.disconnect();
          localStorage.removeItem('token');
        } else {
          if (!opts.skipErrorAlert) {
            store.dispatch(addAlert({
              message: getApiErrorMessage(error),
              severity: 'error',
            }));
          }

          reportApiErrorToSentry(error, {
            url,
            method,
            sentryTags: opts.sentryTags,
            sentryExtra: opts.sentryExtra,
          });
        }

        reject(error);
      })
      .finally(() => {
        store.dispatch(clearLoading());
      });
  });
};
